import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Mail,
  Send,
  Check,
  AlertTriangle,
  Eye,
  EyeOff,
  Loader2,
  Server,
  Cloud,
  Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_LABELS = {
  resend: "Resend",
  sendgrid: "SendGrid",
  mailgun: "Mailgun",
  smtp: "SMTP",
  gmail: "Gmail",
};

export default function MailSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("smtp");
  const [showApiKey, setShowApiKey] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [form, setForm] = useState({
    sender_email: "",
    sender_name: "",
    api_key: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    mailgun_domain: "",
    mailgun_region: "us",
  });
  const [testEmail, setTestEmail] = useState("");

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/org/settings/email`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        if (data?.settings) {
          const s = data.settings;
          setActiveTab(s.provider || "smtp");
          setForm((prev) => ({
            ...prev,
            sender_email: s.sender_email || "",
            sender_name: s.sender_name || "",
            smtp_host: s.smtp_host || "",
            smtp_port: s.smtp_port || 587,
            smtp_username: s.smtp_username || "",
            smtp_use_tls: s.smtp_use_tls !== false,
            mailgun_domain: s.mailgun_domain || "",
            mailgun_region: s.mailgun_region || "us",
          }));
        }
      } else if (res.status === 403) {
        toast.error("You don't have permission to view Mail Settings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const buildPayload = () => {
    const base = {
      provider: activeTab,
      sender_email: form.sender_email.trim(),
      sender_name: form.sender_name.trim(),
    };
    if (activeTab === "resend" || activeTab === "sendgrid") {
      return { ...base, api_key: form.api_key.trim() };
    }
    if (activeTab === "mailgun") {
      return {
        ...base,
        api_key: form.api_key.trim(),
        mailgun_domain: form.mailgun_domain.trim(),
        mailgun_region: form.mailgun_region,
      };
    }
    if (activeTab === "smtp" || activeTab === "gmail") {
      return {
        ...base,
        smtp_host: activeTab === "gmail" ? "smtp.gmail.com" : form.smtp_host.trim(),
        smtp_port: parseInt(form.smtp_port, 10) || 587,
        smtp_username: form.smtp_username.trim(),
        smtp_password: form.smtp_password,
        smtp_use_tls: form.smtp_use_tls,
      };
    }
    return base;
  };

  const handleSave = async () => {
    if (!form.sender_email.trim() || !form.sender_email.includes("@")) {
      toast.error("Sender email is required and must be valid");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/org/settings/email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Saved");
        setForm((prev) => ({ ...prev, api_key: "", smtp_password: "" }));
        fetchSettings();
      } else {
        toast.error(data.detail || "Failed to save", { duration: 8000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear this organization's mail settings? Sending will fall back to platform defaults.")) return;
    const res = await fetch(`${API}/org/settings/email`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      toast.success("Cleared");
      setForm({
        sender_email: "",
        sender_name: "",
        api_key: "",
        smtp_host: "",
        smtp_port: 587,
        smtp_username: "",
        smtp_password: "",
        smtp_use_tls: true,
        mailgun_domain: "",
        mailgun_region: "us",
      });
      fetchSettings();
    }
  };

  const handleTest = async () => {
    if (!testEmail.trim() || !testEmail.includes("@")) {
      toast.error("Enter a valid test email address");
      return;
    }
    setTesting(true);
    try {
      const res = await fetch(`${API}/org/settings/email/test`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ test_email: testEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.detail || "Test failed", { duration: 10000 });
      }
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in-up" data-testid="mail-settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Mail Settings</h1>
        <p className="text-slate-500 mt-1">
          Configure how your organization sends email to leads. Settings are isolated to your organization.
        </p>
      </div>

      {/* Status banner */}
      <Card>
        <CardContent className="p-5 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              {status?.is_configured ? (
                <>
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-slate-900">
                    Active provider: {PROVIDER_LABELS[status?.settings?.provider] || status?.settings?.provider}
                  </span>
                  <Badge variant="secondary" className="text-xs">org-level</Badge>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span className="font-medium text-slate-900">No org-level mail config</span>
                  <Badge variant="outline" className="text-xs">using platform default</Badge>
                </>
              )}
            </div>
            {status?.settings?.sender_email && (
              <p className="text-sm text-slate-500 mt-1">
                Sender: <span className="font-mono">{status.settings.sender_email}</span>
                {status.settings.sender_name && ` (${status.settings.sender_name})`}
              </p>
            )}
          </div>
          {status?.is_configured && (
            <Button variant="ghost" size="sm" className="text-red-600" onClick={handleClear} data-testid="clear-mail-btn">
              <Trash2 className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Provider tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="w-4 h-4" />
            Provider Configuration
          </CardTitle>
          <CardDescription>Pick a provider and fill in credentials for your domain.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 w-full mb-4">
              <TabsTrigger value="smtp" data-testid="tab-smtp"><Server className="w-3.5 h-3.5 mr-1.5" />SMTP</TabsTrigger>
              <TabsTrigger value="sendgrid" data-testid="tab-sendgrid"><Cloud className="w-3.5 h-3.5 mr-1.5" />SendGrid</TabsTrigger>
              <TabsTrigger value="mailgun" data-testid="tab-mailgun"><Cloud className="w-3.5 h-3.5 mr-1.5" />Mailgun</TabsTrigger>
              <TabsTrigger value="gmail" data-testid="tab-gmail"><Mail className="w-3.5 h-3.5 mr-1.5" />Gmail</TabsTrigger>
              <TabsTrigger value="resend" data-testid="tab-resend"><Cloud className="w-3.5 h-3.5 mr-1.5" />Resend</TabsTrigger>
            </TabsList>

            {/* Shared sender fields */}
            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              <div>
                <Label>From Email *</Label>
                <Input
                  type="email"
                  placeholder="info@yourdomain.com"
                  value={form.sender_email}
                  onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                  data-testid="from-email-input"
                />
              </div>
              <div>
                <Label>From Name</Label>
                <Input
                  placeholder="Acme Sales Team"
                  value={form.sender_name}
                  onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                  data-testid="from-name-input"
                />
              </div>
            </div>

            <TabsContent value="smtp" className="space-y-4 mt-0">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label>SMTP Host *</Label>
                  <Input
                    placeholder="smtp.example.com"
                    value={form.smtp_host}
                    onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                    data-testid="smtp-host-input"
                  />
                </div>
                <div>
                  <Label>Port *</Label>
                  <Input
                    type="number"
                    value={form.smtp_port}
                    onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
                    data-testid="smtp-port-input"
                  />
                </div>
              </div>
              <SmtpAuthFields form={form} setForm={setForm} showPwd={showPwd} setShowPwd={setShowPwd} />
            </TabsContent>

            <TabsContent value="gmail" className="space-y-4 mt-0">
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                Gmail uses <span className="font-mono">smtp.gmail.com:587</span> with STARTTLS. You must create an{" "}
                <a className="underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">
                  App Password
                </a>{" "}
                if 2FA is enabled. Use your full Gmail address as the username.
              </div>
              <SmtpAuthFields form={form} setForm={setForm} showPwd={showPwd} setShowPwd={setShowPwd} usernameLabel="Gmail Address" pwdLabel="App Password" />
            </TabsContent>

            <TabsContent value="sendgrid" className="space-y-4 mt-0">
              <ApiKeyField
                label="SendGrid API Key *"
                placeholder="SG.xxxxxxxxxxxxx..."
                hint={status?.settings?.api_key_present ? "A key is already saved. Leave blank to keep it." : "Get yours at app.sendgrid.com/settings/api_keys"}
                value={form.api_key}
                onChange={(v) => setForm({ ...form, api_key: v })}
                show={showApiKey}
                onToggle={() => setShowApiKey((s) => !s)}
                testId="sendgrid-key-input"
              />
            </TabsContent>

            <TabsContent value="mailgun" className="space-y-4 mt-0">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Mailgun Domain *</Label>
                  <Input
                    placeholder="mg.yourdomain.com"
                    value={form.mailgun_domain}
                    onChange={(e) => setForm({ ...form, mailgun_domain: e.target.value })}
                    data-testid="mailgun-domain-input"
                  />
                </div>
                <div>
                  <Label>Region</Label>
                  <select
                    value={form.mailgun_region}
                    onChange={(e) => setForm({ ...form, mailgun_region: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                    data-testid="mailgun-region-input"
                  >
                    <option value="us">US (api.mailgun.net)</option>
                    <option value="eu">EU (api.eu.mailgun.net)</option>
                  </select>
                </div>
              </div>
              <ApiKeyField
                label="Mailgun API Key *"
                placeholder="key-xxxxxxxxxx or full Private API Key"
                hint={status?.settings?.api_key_present ? "A key is already saved. Leave blank to keep it." : "Get yours at app.mailgun.com → Settings → API Keys"}
                value={form.api_key}
                onChange={(v) => setForm({ ...form, api_key: v })}
                show={showApiKey}
                onToggle={() => setShowApiKey((s) => !s)}
                testId="mailgun-key-input"
              />
            </TabsContent>

            <TabsContent value="resend" className="space-y-4 mt-0">
              <ApiKeyField
                label="Resend API Key *"
                placeholder="re_xxxxxxxxxxxx"
                hint={status?.settings?.api_key_present ? "A key is already saved. Leave blank to keep it." : "Get yours at resend.com/api-keys"}
                value={form.api_key}
                onChange={(v) => setForm({ ...form, api_key: v })}
                show={showApiKey}
                onToggle={() => setShowApiKey((s) => !s)}
                testId="resend-key-input"
              />
            </TabsContent>
          </Tabs>

          <div className="flex gap-2 pt-4 border-t mt-6">
            <Button onClick={handleSave} disabled={saving} data-testid="save-mail-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save {PROVIDER_LABELS[activeTab]} Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test email */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="w-4 h-4" />
            Send Test Email
          </CardTitle>
          <CardDescription>Verify your settings by sending a test message.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder="you@yourdomain.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1"
              data-testid="test-email-input"
            />
            <Button onClick={handleTest} disabled={testing} data-testid="send-test-email-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SmtpAuthFields({ form, setForm, showPwd, setShowPwd, usernameLabel = "Username", pwdLabel = "Password" }) {
  return (
    <>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>{usernameLabel}</Label>
          <Input
            value={form.smtp_username}
            onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
            data-testid="smtp-username-input"
          />
        </div>
        <div>
          <Label>{pwdLabel}</Label>
          <div className="relative">
            <Input
              type={showPwd ? "text" : "password"}
              value={form.smtp_password}
              onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
              className="pr-10"
              placeholder={form.smtp_username ? "Leave blank to keep existing" : ""}
              data-testid="smtp-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Switch
          checked={form.smtp_use_tls}
          onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v })}
          data-testid="smtp-tls-switch"
        />
        <Label className="cursor-pointer" onClick={() => setForm({ ...form, smtp_use_tls: !form.smtp_use_tls })}>
          Use STARTTLS encryption (recommended)
        </Label>
      </div>
    </>
  );
}

function ApiKeyField({ label, placeholder, hint, value, onChange, show, onToggle, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono pr-10"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
